<template>
  <v-container fluid>
    <v-card>
      <v-toolbar color="primary" density="comfortable">
        <v-toolbar-title class="text-white">Véhicules</v-toolbar-title>
        <v-spacer />
        <div class="d-flex ga-2">
          <table-header-buttons
            :attr="toolTipText"
            @add="add"
            @remove="remove"
            @reload="reloadData"
            :has-selected-lines="selectedVehicleIds.length > 0"
          ></table-header-buttons>
        </div>
      </v-toolbar>
      <Table
        :loading="loading"
        :search-field="true"
        :columns="columns"
        :items="vehicles"
        :show-select="true"
        storageKey="vehicleTablePaginationState"
        v-model:selected="selectedVehicleIds"
        @edit="edit"
        @row-clicked="rowClicked"
        @selectionChanged="selectionChanged"
      ></Table>
    </v-card>
    <v-overlay :model-value="loading" class="align-center justify-center">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>

    <ConfirmDialog
      v-model="dialog"
      :dialog-title="dialogTitle"
      :dialog-text="dialogText"
      @confirm="onConfirm"
      @cancel="onCancel"
    ></ConfirmDialog>
  </v-container>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import { useRouter } from "vue-router";
import Table from "@/components/Table.vue";
import TableHeaderButtons from "@/components/TableHeaderButtons.vue";
import ConfirmDialog from "@/components/ConfirmDialog.vue";
import { useAppStore } from "@/store/app";
import type { TableColumn, Vehicle } from "@/types";
import { deleteVehicle, getAllVehicles } from "@/utils/vehicle-api";

const router = useRouter();
const appStore = useAppStore();

const dialog = ref(false);
const dialogTitle = ref("Suppression");
const dialogText = ref("Voulez-vous supprimer les comptes siège sélectionnés?");
const showSearchPanel = ref(false);
const loading = ref(false);
const vehicles = ref<Vehicle[]>([]);
const selectedVehicleIds = ref<string[]>([]);
const query = ref("");
const toolTipText = "un véhicule";
const columns: TableColumn[] = [
  { title: "Libellé", key: "label", align: "start" },
  { title: "Immatriculation", key: "plateNumber" },
];

const selectionChanged = (selected: string[]) => {
  selectedVehicleIds.value = [...selected];
};

const rowClicked = (item: Vehicle) => {
  router.push(`/vehicles/${item.id}`);
};

const edit = (item: Vehicle) => {
  router.push(`/vehicles/${item.id}/edit`);
};

const add = () => {
  router.push("/NewVehicle");
};

const remove = () => {
  dialog.value = true;
};

const onConfirm = async () => {
  try {
    loading.value = true;
    dialog.value = false;
    await deleteVehicle(selectedVehicleIds.value.findLast((id) => id !== undefined));
    await fetchVehicles();
    selectedVehicleIds.value = [];
    appStore.sendSuccessNotice("Vehicle has been deleted.");
  } finally {
    loading.value = false;
  }
};

const onCancel = () => {
  dialog.value = false;
};

const reloadData = () => {
  query.value = "";
  fetchVehicles();
};

const fetchVehicles = async () => {
  try {
    loading.value = true;
    vehicles.value = await getAllVehicles();
  } finally {
    loading.value = false;
  }
};

onMounted(() => {
  fetchVehicles();
});
</script>
<style scoped>
.title {
  font-size: 1.5rem;
  font-weight: bold;
}
</style>
